import { GetOneExpeditionBoResponseDto } from "@/types/api/api-types";
import { OtherCustomQuotationTypeEnum } from "@/constants/enums/otherCustomQuotationType.enum";
import { ParcelProductTypeEnum } from "@/constants/enums/parcelProductType.enum";
import { ReCustomQuotationFormData } from "../customQuotationQuote.form";

export const getCustomQuotationQuoteDefault = (expedition: GetOneExpeditionBoResponseDto) => {
  const packageExpeditionValue: number | null = expedition.customQuotation?.parcelDetail
    ? expedition.customQuotation.parcelDetail.parcelPackages.reduce(
        (sum, pkg) => sum + pkg.value,
        0,
      )
    : expedition.customQuotation?.paletteDetail
      ? expedition.customQuotation.paletteDetail.palettePackages.reduce(
          (sum, pkg) => sum + pkg.value,
          0,
        )
      : null;

  return {
    invoiceData: [
      {
        client: expedition.deliveryInfo?.firstName
          ? expedition.deliveryInfo?.firstName + " " + expedition.deliveryInfo?.lastName
          : expedition.deliveryInfo?.company || undefined,
        customId: expedition.customQuotation?.customId || undefined,
        reference: expedition.expeditionBillingDetail?.reference || undefined,
        transportHt: expedition.expeditionBillingDetail?.expeditionInvoiceData
          ? expedition.expeditionBillingDetail?.expeditionInvoiceData.transportHt / 100
          : undefined,
        packaging: expedition.expeditionBillingDetail?.expeditionInvoiceData
          ? expedition.expeditionBillingDetail?.expeditionInvoiceData?.packaging / 100
          : undefined,
        packagingProtection: expedition.expeditionBillingDetail?.expeditionInvoiceData
          ? expedition.expeditionBillingDetail?.expeditionInvoiceData?.packagingProtection / 100
          : undefined,
        taxes: expedition.expeditionBillingDetail?.expeditionInvoiceData
          ? expedition.expeditionBillingDetail?.expeditionInvoiceData?.taxes / 100
          : undefined,
        insurance: expedition.expeditionBillingDetail?.expeditionInvoiceData
          ? expedition.expeditionBillingDetail?.expeditionInvoiceData?.insurance / 100
          : undefined,
        tva: expedition.expeditionBillingDetail?.expeditionInvoiceData
          ? expedition.expeditionBillingDetail?.expeditionInvoiceData?.tva / 100
          : undefined,
        merchandiseValue:
          packageExpeditionValue ?? expedition.customQuotation?.otherDetail?.value ?? 0,
        insuranceTvaOverride:
          expedition.expeditionBillingDetail?.expeditionInvoiceData?.insuranceTvaOverride ?? false,
      },
    ],
  };
};

export const getReCustomQuotationDefault = (
  expedition: GetOneExpeditionBoResponseDto,
): ReCustomQuotationFormData => {
  const reference = "RE-EXP-D-" + expedition.customQuotation?.identifier;

  return {
    invoiceData: {
      client: expedition.deliveryInfo?.firstName
        ? expedition.deliveryInfo?.firstName + " " + expedition.deliveryInfo?.lastName
        : expedition.deliveryInfo?.company || "",
      customId: expedition.customQuotation?.customId || "",
      reference: reference,
      transportHt: expedition.expeditionBillingDetail?.expeditionInvoiceData
        ? expedition.expeditionBillingDetail?.expeditionInvoiceData.transportHt / 100
        : 0,
      packaging: expedition.expeditionBillingDetail?.expeditionInvoiceData
        ? expedition.expeditionBillingDetail?.expeditionInvoiceData.packaging / 100
        : 0,
      packagingProtection: expedition.expeditionBillingDetail?.expeditionInvoiceData
        ? expedition.expeditionBillingDetail?.expeditionInvoiceData.packagingProtection / 100
        : 0,
      taxes: expedition.expeditionBillingDetail?.expeditionInvoiceData
        ? expedition.expeditionBillingDetail?.expeditionInvoiceData.taxes / 100
        : 0,
      insurance: expedition.expeditionBillingDetail?.expeditionInvoiceData
        ? expedition.expeditionBillingDetail?.expeditionInvoiceData.insurance / 100
        : 0,
      tva: expedition.expeditionBillingDetail?.expeditionInvoiceData
        ? expedition.expeditionBillingDetail?.expeditionInvoiceData.tva / 100
        : 0,
      merchandiseValue: expedition.customQuotation?.otherDetail?.value || 0,
    },
    ...(expedition.pickupInfo?.pickupLocation
      ? {
          pickupInfo: {
            manualPickup: false,
            pickupLocationId: expedition.pickupInfo?.pickupLocation.id,
          },
        }
      : {
          pickupInfo: {
            manualPickup: true,
            pickupFirstName: expedition.pickupInfo?.pickupFirstName || "",
            pickupLastName: expedition.pickupInfo?.pickupLastName || "",
            pickupPhone: expedition.pickupInfo?.pickupPhone || "",
            pickupCompany: expedition.pickupInfo?.pickupCompany || "",
            pickupStreet: expedition.pickupInfo?.pickupStreet || "",
            pickupPostalCode: expedition.pickupInfo?.pickupPostalCode || "",
            pickupCity: expedition.pickupInfo?.pickupCity || "",
            pickupCountry: expedition.pickupInfo?.pickupCountry || "",
          },
        }),
    billingEntityId: expedition.expeditionBillingDetail?.billingEntityId || "",
    express: expedition.expeditionOptions?.express || false,
    insurance: expedition.expeditionOptions?.insurance || false,
    ...(expedition.customQuotation?.otherDetail?.isMultipleExpedition
      ? {
          deliveryInfo: {
            singleDelivery: false,
            otherNumberOfExpedition:
              expedition.customQuotation?.otherDetail?.numberOfExpedition || 0,
          },
        }
      : {
          deliveryInfo: {
            singleDelivery: true,
            deliveryLastName: expedition.deliveryInfo?.lastName || "",
            deliveryFirstName: expedition.deliveryInfo?.firstName || "",
            deliveryPhone: expedition.deliveryInfo?.phone || "",
            deliveryEmail: expedition.deliveryInfo?.email || "",
            deliveryCompany: expedition.deliveryInfo?.company || "",
            deliveryStreet: expedition.deliveryInfo?.street || "",
            deliveryPostalCode: expedition.deliveryInfo?.postalCode || "",
            deliveryCity: expedition.deliveryInfo?.city || "",
            stateOrProvinceCode: expedition.deliveryInfo?.state || "",
            countryCode: expedition.deliveryInfo?.country || "",
            deliveryAdditionalInfo: expedition.deliveryInfo?.additionalInfo || "",
            deliveryAdditionalInfo2: expedition.deliveryInfo?.additionalInfo2 || "",
            customId: expedition.customQuotation?.customId || "",
          },
        }),
    reference: reference,
    otherCustomQuotationType: expedition.customQuotation?.otherDetail
      ?.otherCustomQuotationType as OtherCustomQuotationTypeEnum,
    parcelProducts: expedition.customQuotation?.parcelDetail?.parcelProducts.map((product) => ({
      type: product.type as ParcelProductTypeEnum,
      volume: product.volume,
      bottleQuantity: product.bottleQuantity,
      description: product.description || "",
    })),
    parcelPackages: expedition.customQuotation?.parcelDetail?.parcelPackages.map((elem) => ({
      length: elem.length,
      width: elem.width,
      height: elem.height,
      weight: elem.weight,
      value: elem.value,
    })),
    paletteProducts: expedition.customQuotation?.paletteDetail?.paletteProducts.map((product) => ({
      volume: product.volume,
      bottleQuantity: product.bottleQuantity,
    })),
    palettePackages: expedition.customQuotation?.paletteDetail?.palettePackages.map((elem) => ({
      length: elem.length,
      width: elem.width,
      height: elem.height,
      weight: elem.weight,
      value: elem.value,
    })),
    basedOnId: expedition.id,
    skipPickup: undefined as unknown as boolean,
    note: "",
    customId: expedition.customQuotation?.customId || "",
    transportDiscountPercentage: undefined as unknown as number,
    overallDiscountPercentage: undefined as unknown as number,
  };
};
